import { FC, ReactNode, useMemo } from 'react';
import { NumberUtils, Tr } from '@utils';
import { Box, Stack, StackProps, Typography, TypographyProps } from '@mui/material';
import { FleetVehicle, SimulationVehicle } from '@services';
import { VehicleImage } from '@components/portal/common';

const t = (key: string) => `conversion-list.vehicle-comparison.${key}`;

interface IInfoBoxProps extends Omit<StackProps, 'title'> {
	title?: ReactNode;
	subtitle?: ReactNode;
	titleProps?: TypographyProps;
	subtitleProps?: TypographyProps;
}

const InfoBox: FC<IInfoBoxProps> = ({
	title,
	subtitle,
	sx,
	titleProps: { sx: titleSx, ...titleProps } = {},
	subtitleProps: { sx: subtitleSx, ...subtitleProps } = {},
	...props
}) => {
	return title || subtitle ? (
		<Stack direction="column" alignItems="center" sx={{ width: 1.0, paddingX: 6.5, paddingY: 2, ...sx }} {...props}>
			{title ? (
				<Typography variant="h4" sx={{ ...titleSx, fontWeight: '600', textAlign: 'center' }} {...titleProps}>
					{title}
				</Typography>
			) : null}
			{subtitle ? (
				<Typography sx={{ ...subtitleSx, textAlign: 'center' }} {...subtitleProps}>
					{subtitle}
				</Typography>
			) : null}
		</Stack>
	) : null;
};

interface Base extends Omit<StackProps, 'title'> {
	title?: ReactNode;
	vehicle: FleetVehicle;
	color?: string;
	primaryColor?: string;
	secondaryColor?: string;
	selectedPrimaryColor?: string;
	selectedSecondaryColor?: string;
	selected?: boolean;
	vehiclePriceVisible?: boolean;
}
interface ModeSelf {
	mode: 'self';
	simulationVehicle?: undefined;
}
interface ModeCompare {
	mode: 'compare';
	simulationVehicle: SimulationVehicle;
}

type Mode = ModeSelf | ModeCompare;

export type ICarModelInfoProps = Base & Mode;

export const CarModelInfo: FC<ICarModelInfoProps> = ({
	title,
	vehicle,
	simulationVehicle,
	color = 'common.black',
	primaryColor = 'grey.100',
	secondaryColor = 'grey.50',
	mode = 'compare',
	vehiclePriceVisible,
	sx,
	...props
}) => {
	const standardSx: IInfoBoxProps['sx'] = { color };
	const lightSx: IInfoBoxProps['sx'] = { color, backgroundColor: secondaryColor };
	const rows = useMemo<IInfoBoxProps[]>(() => {
		const carModel = mode === 'compare' ? simulationVehicle?.carModel : vehicle?.carModel;
		const { fromStock, stockPrice } = simulationVehicle || {};
		const { brand, model, version, batteryCapacity, price } = carModel || {};
		const vehiclePrice = fromStock ? stockPrice : price;
		const brandModel = [[brand, model, '-', version].filter(Boolean).join(' '), mode === 'self' ? vehicle?.plateNumber : undefined]
			.filter(Boolean)
			.join(' - ');
		const tco = mode === 'self' ? vehicle.totalCostOfOwnership : simulationVehicle?.totalCostOfOwnership;
		const co2row =
			(mode === 'compare' ? (vehicle.co2Emission || 0) - (simulationVehicle?.co2emission || 0) : vehicle.co2Emission || 0) / 1000000;
		const fuelRow =
			mode === 'compare' ? (vehicle.yearlyFuelCost || 0) - (simulationVehicle?.fuelCost || 0) : vehicle.yearlyFuelCost || 0;

		return [
			{
				subtitle: brandModel,
				subtitleProps: { sx: { textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: 1.0 } }
			},
			{
				title: NumberUtils.formatAsNumber(co2row, { decimalScale: 2, suffix: 'ton', pureDefault: true, defaultValue: '-' }),
				subtitle: <Tr.Portal path={t(mode === 'compare' ? 'co2-reduced-year' : 'co2-consumption-year')} />
			},
			{
				title: NumberUtils.formatAsPrice(fuelRow, { decimalScale: 0, pureDefault: true, defaultValue: '-' }),
				subtitle: <Tr.Portal path={t(mode === 'compare' ? 'fuel-reduced-year' : 'fuel-consumption-year')} />
			},
			vehiclePriceVisible
				? {
						title: NumberUtils.formatAsPrice(tco, { decimalScale: 0, pureDefault: true, defaultValue: '-' }),
						subtitle: <Tr.Portal path={t('tco')} />
				  }
				: undefined,
			vehiclePriceVisible
				? {
						title: NumberUtils.formatAsPrice(vehiclePrice, { decimalScale: 0, pureDefault: true, defaultValue: '-' }),
						subtitle: <Tr.Portal path={t('catalog-price')} />
				  }
				: undefined,
			{
				title: NumberUtils.formatAsNumber(batteryCapacity, {
					decimalScale: 2,
					suffix: 'KW/h',
					pureDefault: true,
					defaultValue: '-'
				}),
				subtitle: <Tr.Portal path={t('battery-size')} />
			}
		].filter(Boolean) as IInfoBoxProps[];
	}, [mode, vehicle, simulationVehicle]);

	return (
		<Stack
			direction="column"
			alignItems="center"
			sx={{ width: 1.0, height: 1.0, overflow: 'hidden', backgroundColor: primaryColor, ...sx }}
			{...props}
		>
			{title ? <Box sx={{ width: 1.0, paddingX: 6.5 }}>{title}</Box> : null}
			<VehicleImage
				model={mode === 'compare' ? simulationVehicle?.carModel : vehicle.carModel}
				style={{ width: '100%', height: 'auto' }}
				containerProps={{ sx: { width: 1.0, paddingX: 0, paddingY: 2 } }}
			/>
			{rows.map(({ ...rowProps }, i) => (
				<InfoBox key={`info-box-${i}`} sx={{ ...(i % 2 ? lightSx : standardSx) }} {...rowProps} />
			))}
		</Stack>
	);
};
